import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import ErrorBoundary from "./components/ErrorBoundary";
import { initSentry, reportError } from "./lib/sentry";
import { instrumentFetch } from "./lib/instrument-fetch";

// Initialize Sentry before mounting React so the earliest render-time
// errors are captured. No-op in dev / when VITE_SENTRY_DSN is unset.
initSentry();
instrumentFetch();

// One-time auto-reload for stale Vite chunk references after a redeploy/HMR
// churn. The browser is holding a hash that no longer exists; a single hard
// reload picks up the new manifest. Guarded by sessionStorage so we never loop.
const handleStaleChunk = (message: string) => {
  if (typeof window === 'undefined') return;
  const isChunkError =
    /Importing a module script failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message);
  if (!isChunkError) return;
  // Time-based guard: allow another reload after 10s so a second stale-chunk
  // later in the session still recovers (instead of leaving a blank screen).
  const last = Number(sessionStorage.getItem('chunk-reload-at') || '0');
  if (Date.now() - last < 10_000) return;
  sessionStorage.setItem('chunk-reload-at', String(Date.now()));
  window.location.reload();
};

window.addEventListener('error', (event) => {
  handleStaleChunk(event.message ?? '');
  if (event.error) reportError(event.error, { kind: 'window.error' });
});
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const message = typeof reason === 'string' ? reason : reason?.message ?? '';
  handleStaleChunk(message);
  reportError(reason, { kind: 'unhandledrejection' });
});

// Global image error handler — adds fallback styling with alt text
document.addEventListener('error', (e) => {
  const target = e.target;
  if (target instanceof HTMLImageElement && !target.dataset.fallbackApplied) {
    target.dataset.fallbackApplied = 'true';
    target.classList.add('img-fallback-error');
    // Surface alt text inside the broken image box via a data attribute
    if (target.alt) {
      target.dataset.altLabel = target.alt;
    }
  }
}, true); // capture phase to catch all img errors

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);
